import { CommonModule } from '@angular/common';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { Component, OnInit, inject, OnChanges, SimpleChanges } from '@angular/core';
import { FirebaseFirestoreService } from '../../services/firebase-firestore.service';
import {
  BehaviorSubject,
  distinctUntilChanged,
  filter,
  first,
  firstValueFrom,
  map,
  startWith,
  switchMap,
  take,
  delay,
  tap,
  catchError,
  of
} from 'rxjs';
import { GroupFolder, GroupRenderData } from '../../services/firebase.model';
import { ConfirmationService, MenuItem, MessageService, TreeNode } from 'primeng/api';
import { TreeModule, TreeNodeSelectEvent } from 'primeng/tree';
import { PanelModule } from 'primeng/panel';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { RenderCardComponent } from '../../shared/render-card/render-card.component';
import { DataViewModule } from 'primeng/dataview';
import { DialogService } from 'primeng/dynamicdialog';
import { MoveFolderDialogComponent } from '../move-folder-dialog/move-folder-dialog.component';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { Router } from '@angular/router';
import { ContextMenuModule } from 'primeng/contextmenu';
import { ToastModule } from 'primeng/toast';
import { Store } from '@ngrx/store';
import { BaseState } from '../../reducers';
import { selectActivePipeline } from '../../users/store/user.selector';
import { PipelineResponses } from '../../render-list-page/render-list/render-list.component';
import { RenderPipelineDialogComponent } from '../../render-list-page/render-pipeline-dialog/render-pipeline-dialog.component';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CommentsDialogComponent } from '../comments-dialog/comments-dialog.component';
import { NameDialogComponent } from '../new-folder-dialog/name-dialog.component';

@Component({
  selector: 'app-enterprise-view',
  standalone: true,
  imports: [
    CommonModule,
    TreeModule,
    PanelModule,
    ButtonModule,
    CardModule,
    RenderCardComponent,
    DataViewModule,
    ConfirmDialogModule,
    ContextMenuModule,
    ToastModule,
    ProgressSpinnerModule
  ],
  templateUrl: './enterprise-view.component.html',
  styleUrl: './enterprise-view.component.scss',
  providers: [
    DialogService,
    ConfirmationService,
    MessageService
  ]
})
export class EnterpriseViewComponent implements OnInit {
  firestoreService = inject(FirebaseFirestoreService)
  dialogService = inject(DialogService)
  confirmationService = inject(ConfirmationService)
  router = inject(Router)
  messageService = inject(MessageService)
  store = inject(Store<BaseState>)

  newPipelineRequested = false

  renderPipelineId$ = this.store.select(selectActivePipeline)
  selected$: BehaviorSubject<TreeNode> = new BehaviorSubject({})
  _pTreeBoundSeletion: TreeNode
  folders$ = this.firestoreService.getGroupFolders()
  folderMap$ = this.folders$.pipe(
    map(({folders}) => {
      const folderMap = new Map<string, GroupFolder[]>()
      folders.forEach(x => {
        const group = folderMap.get(x.parentFolder) || []
        group.push(x)
        folderMap.set(x.parentFolder, group)
      })
      return folderMap
    })
  )

  renders$ = this.selected$.pipe(
    distinctUntilChanged(),
    filter(x => !!x.key),
    switchMap(selected => {
      return this.firestoreService.getGroupRenders(selected.key as string).pipe(
        delay(300),
        map(renders => ({
          loading: false,
          data: renders ?? [],
          error: null
        })),
        startWith({
          loading: true,
          data: null,
          error: null
        }),
        catchError(error => of({
          loading: false,
          data: null,
          error
        }))
      );
    }),
    startWith({
      loading: true,
      data: null,
      error: null
    })
  );

  treeView$ = this.folderMap$.pipe(
    map((folderMap) => {
      const root: TreeNode<GroupFolder> = {
        label: 'Home',
        icon: 'pi fi fi-rr-house-chimney',
        type: "folder",
        key: "root",
        selectable: true,
        expanded: true,
        children: []
      }

      const fillChildrenRecursivly = (folder: TreeNode) => {
        const folderChildren = folderMap.get(folder.key as string) || []

        const childrenFolder: TreeNode<GroupFolder>[] = folderChildren.map(x => ({
          label: x.name,
          collapsedIcon: 'pi fi fi-rr-folder',
          expandedIcon: 'pi fi fi-rr-folder-open',
          key: x.id,
          data: x,
          type: "folder",
          selectable: true,
          expanded: false,
          children: []
        }))
        folder.children = [...childrenFolder]
        childrenFolder.forEach(x => fillChildrenRecursivly(x))
      }
      fillChildrenRecursivly(root)
      const rootChildren = root.children || []
      root.children = []
      return [root, ...rootChildren]
    })
  )

  onNodeUnselected(event: TreeNodeSelectEvent) {
    setTimeout(() => {
      this.selected$.next(event.node)
    })
  }

  onNodeSelected(event: TreeNodeSelectEvent) {
    this.selected$.next(event.node)
  }

  ngOnInit(): void {
    this.treeView$.pipe(
      filter(x => !!x),
      first()
    ).subscribe(x => {
        this.selected$.next(x[0])
      })

    this.selected$.subscribe(x => this._pTreeBoundSeletion = x)
  }

  moveCard(card: GroupRenderData) {
    this.dialogService.open(MoveFolderDialogComponent, {
      header: `Move: ${card.renderName}`,
      width: '450px',
      data: {
        treeView$: this.treeView$
      }
    }).onClose.pipe(
      filter(x => !!x),
      first()
    ).subscribe((node: TreeNode) => {
      this.firestoreService.moveGroupRender({...card, parentFolder: node.key as string})
    })
  }

  newFolder() {
    const currentFolder = this.selected$.getValue()
    if (!currentFolder) {
      return
    }
    this.dialogService.open(NameDialogComponent, {
      header: `Name Folder`,
      width: '450px',
      data: {
        textInput: "Folder Name",
        completedText: "Create",
        completedIcon: "pi fi fi-rr-add-folder"
      }
    }).onClose.pipe(
      filter(x => !!x),
      first()
    ).subscribe((name: string) => {
        this.firestoreService.newGroupFolder(currentFolder.key as string, name)
    })
  }

  renameFolder() {
    const currentFolder = this.selected$.getValue()
    if (!currentFolder) {
      return
    }
    this.dialogService.open(NameDialogComponent, {
      header: 'Rename Folder',
      width: '450px',
    }).onClose.pipe(
      filter(x => !!x),
      first()
    ).subscribe((name: string) => {
      this.firestoreService.renameFolderGroup(currentFolder.data, name)
    })

  }

  deleteCard(card: GroupRenderData) {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this render? This action cannot be undone.',
      header: 'Confirm Delete',
      acceptIcon:"none",
      rejectIcon:"none",
      acceptLabel: 'Delete Render',
      rejectLabel: 'Cancel',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.firestoreService.deleteGroupRender(card)
    })
  }

  trackByFolder(_index: number, folder: TreeNode<GroupFolder>) {
    return folder.key
  }

  async deleteFolder() {
    const folderToDelete: GroupFolder = this.selected$.getValue().data
    if (folderToDelete.id === "root") {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Cannot delete root folder'})
    }

    const renders = await firstValueFrom(this.firestoreService.getGroupRenders(folderToDelete.id))
    if (renders.length) {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Cannot delete a folder containing renders'})
      return
    }
    const folders = await firstValueFrom(this.folders$)
    if (folders.folders.some(x => x.parentFolder === folderToDelete.id)) {
      this.messageService.add({severity: 'error', summary: 'Error', detail: 'Cannot delete a folder containing folders'})
      return
    }
    this.firestoreService.deleteGroupFolder(folderToDelete.id)
  }

  cloneCard(card: GroupRenderData) {
    this.firestoreService.cloneGroupRender(card)
  }

  showComments(card: GroupRenderData) {
    this.dialogService.open(CommentsDialogComponent,{
      data: {
        comments: card.comments,
        renderId: card.id
      },
      header: 'Comments',
    })
  }

  openCard(card: GroupRenderData) {
    this.router.navigate(['enterprise', card.id])
  }


  treeOptions: MenuItem[] =  [
    { label: 'Rename', icon: 'pi fi fi-rr-file-edit', command: () => {}},
    { label: 'Delete', icon: 'pi pi-trash', command: (event$) => this.deleteFolder()},
  ]

  async openPipelinesRequest(): Promise<PipelineResponses> {
    if (!this.newPipelineRequested) {
      try {
        this.newPipelineRequested = true
        const response = await httpsCallable(
          getFunctions(),
          'openGroupRenderPipeline'
        )({
            folderId: this.selected$.getValue().key
        })
        return PipelineResponses.success
      }
      catch (e: any) {
        if (e.code === 'functions/unavailable') {
          return PipelineResponses.insufficientFunds
        }
        this.messageService.add({severity: 'warn', summary: e.code, detail: e.message});
        this.newPipelineRequested = false
        return PipelineResponses.error
      }
    }
    return PipelineResponses.error
  }

  openRenderStatusOnceRenderIdValid() {
    this.renderPipelineId$.pipe(
      delay(1000),
      filter(x => !!x),
      tap(x => console.log(x)),
      take(1)
    ).subscribe(_ => {
        this.openRenderStatus()
        this.newPipelineRequested = false
      }
    )
  }

  async openPipeline() {
    const activePipelineId = await firstValueFrom(this.renderPipelineId$)
    if (!activePipelineId) {
      const resolved = await this.openPipelinesRequest()
      if (resolved === PipelineResponses.success) {
        this.openRenderStatusOnceRenderIdValid()
      }
    }
    else {
      this.confirmationService.confirm({
        message: 'A render pipeline is already open. Do you want to close the active render pipeline and open another?',
        header: 'Render Pipeline Conflict',
        icon: 'pi pi-exclamation-circle',
        acceptLabel: 'New Pipeline',
        acceptIcon: 'pi pi-sync',
        acceptButtonStyleClass: 'accept-button',
        rejectLabel: 'Cancel',
        rejectButtonStyleClass: 'reject-button',
        accept: () => {
          this.openPipelinesRequest()
          this.openRenderStatusOnceRenderIdValid()
        }
      })
    }
  }

  openRenderStatus() {
    this.dialogService.open(RenderPipelineDialogComponent, {
      header: 'Render Status',
      width: 'min(50%, 500px)',
    })
  }
}
